import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Capacitor } from '@capacitor/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { ToastService } from '../../../services/toast.service';
import { firstValueFrom } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { CheckboxModule } from 'primeng/checkbox';
import { RippleModule } from 'primeng/ripple';
import { DrawerModule } from 'primeng/drawer';
import { environment } from 'src/environments/environment';
import { DividerModule } from 'primeng/divider';
import { firebaseAuthTranslation } from 'src/app/util/FirebaseErrorTranslation';
import { PASSWORD_VALIDATE_REGEX, hasUppercase, hasLowercase, hasMinLength } from 'src/app/util/PasswordUtil';
import { validatePasswordMatch } from 'src/app/util/Validators';
import { MessageModule } from 'primeng/message';
import { IonContent, IonModal, NavController, Platform } from "@ionic/angular/standalone";
import { ProfileDataService } from 'src/app/services/profile-data.service';

type AuthStep = 'email' | 'password';

@Component({
  selector: 'app-login',
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
  standalone: true,
  imports: [
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    PasswordModule,
    FormsModule,
    RippleModule,
    ReactiveFormsModule,
    DrawerModule,
    CommonModule,
    DividerModule,
    MessageModule,
    IonContent,
    IonModal
  ],
})
export class LoginPage implements OnInit {
  // Step management
  currentStep: AuthStep = 'email';
  isNewUser = false;
  enteredEmail = '';

  // Loading states
  isCheckingEmail = false;
  isAuthInitiated = false;
  isPasswordResetRequestVisible = false;
  isPasswordResetRequestInitiated = false;
  isPasswordResetVisible = false;
  isPasswordResetInitiated = false;
  isGoogleLoginInitiated = false;
  isAppleLoginInitiated = false;

  environment = environment;

  // Apple Sign-In nur auf iOS anzeigen
  showAppleLogin = Capacitor.getPlatform() === 'ios' || !Capacitor.isNativePlatform();

  // Step 1: Email form
  emailForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
  });

  // Step 2: Password form (dynamisch für Login oder Register)
  passwordForm = new FormGroup({
    password: new FormControl('', [Validators.required]),
    toc: new FormControl(false),
  });

  passwordResetRequestForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
  });

  passwordResetForm = new FormGroup(
    {
      password: new FormControl('', [
        Validators.required,
        Validators.pattern(PASSWORD_VALIDATE_REGEX),
      ]),
      passwordConfirm: new FormControl('', [
        Validators.required,
        Validators.pattern(PASSWORD_VALIDATE_REGEX),
      ]),
    },
    {
      validators: validatePasswordMatch,
    },
  );

  hasUppercase = hasUppercase;
  hasLowercase = hasLowercase;
  hasMinLength = hasMinLength;

  constructor(
    private _auth: AuthService,
    private _toast: ToastService,
    private _route: ActivatedRoute,
    private _nav: NavController,
    private _profile: ProfileDataService,
    private platform: Platform
  ) { }

  async ngOnInit() {
    const mode = this._route.snapshot.queryParams['mode'];
    if (mode == 'resetPassword') {
      this.isPasswordResetVisible = true;
    } else if (mode == 'verifyEmail') {
      this.verifyEmail();
    } else {
      if (this._auth.user$) {
        const user = await firstValueFrom(this._auth.user$);
        if (user) {
          this._nav.navigateRoot(['/dashboard']);
        }
      }
    }

    // Check for registration code
    const registrationCode = this._route.snapshot.queryParams['registrationCode'];
    if (registrationCode) {
      localStorage.setItem('registrationCode', registrationCode);
    }
  }

  /**
   * Step 1: Email prüfen – automatisch Login oder Register bestimmen
   */
  async checkEmail() {
    if (this.emailForm.invalid) {
      this.emailForm.markAllAsTouched();
      this._toast.add({
        severity: 'contrast',
        summary: 'Bitte gültige E-Mail eingeben',
      });
      return;
    }

    this.isCheckingEmail = true;
    this.enteredEmail = this.emailForm.value.email!;

    try {
      const emailExists = await firstValueFrom(
        this._auth.checkEmailExists(this.enteredEmail)
      );
      console.log("hallo", emailExists);
      

      this.isNewUser = !emailExists;

      if (this.isNewUser) {
        this.setupPasswordFormForRegistration();
      } else {
        this.setupPasswordFormForLogin();
      }
    } catch (error) {
      // Fallback: Login anzeigen
      this.isNewUser = false;
      this.setupPasswordFormForLogin();
    }

    // Prefill password reset form with email
    this.passwordResetRequestForm.patchValue({ email: this.enteredEmail });

    this.currentStep = 'password';
    this.isCheckingEmail = false;
  }

  /**
   * Wechselt zwischen Login und Registration Modus
   */
  toggleAuthMode() {
    this.isNewUser = !this.isNewUser;
    const currentPassword = this.passwordForm.get('password')?.value || '';

    if (this.isNewUser) {
      this.setupPasswordFormForRegistration();
    } else {
      this.setupPasswordFormForLogin();
    }

    // Passwort beibehalten
    this.passwordForm.patchValue({ password: currentPassword });
  }

  private setupPasswordFormForLogin() {
    this.passwordForm = new FormGroup({
      password: new FormControl('', [Validators.required]),
      toc: new FormControl(false),
    });
  }

  private setupPasswordFormForRegistration() {
    this.passwordForm = new FormGroup({
      password: new FormControl('', [
        Validators.required,
        Validators.pattern(PASSWORD_VALIDATE_REGEX),
      ]),
      toc: new FormControl(false, [Validators.requiredTrue]),
    });
  }

  goBackToEmail() {
    this.currentStep = 'email';
    this.passwordForm.reset();
  }

  /**
   * Step 2: Login oder Registration basierend auf isNewUser
   */
  async submitAuth() {
    if (this.isNewUser) {
      await this.performRegistration();
    } else {
      await this.performLogin();
    }
  }

  private async performLogin() {
    if (this.passwordForm.get('password')?.invalid) {
      this._toast.add({
        severity: 'contrast',
        summary: 'Bitte Passwort eingeben',
      });
      return;
    }

    this.isAuthInitiated = true;
    this.passwordForm.disable();

    try {
      await firstValueFrom(
        this._auth.login(
          this.enteredEmail,
          this.passwordForm.value.password!,
        ),
      );
      await this._profile.loadUserDetails();
      this._nav.navigateRoot(['/dashboard']);
    } catch (error: any) {
      this.isAuthInitiated = false;
      this.passwordForm.enable();

      console.error(error);

      const errorTranslation = firebaseAuthTranslation[error.code];

      if (errorTranslation?.toDev) console.error(errorTranslation.toDev);
      this._toast.add({
        severity: 'contrast',
        summary: 'Fehler beim Login',
        detail: errorTranslation?.toClient ?? 'Interner Serverfehler',
      });
    }
  }

  private async performRegistration() {
    // Validate password
    const passwordControl = this.passwordForm.get('password');
    if (passwordControl?.invalid) {
      if (passwordControl?.hasError('pattern')) {
        this._toast.add({
          severity: 'contrast',
          summary: 'Passwort zu schwach',
          detail: 'Verwende mindestens 8 Zeichen mit Groß-/Kleinbuchstaben, Zahlen und Sonderzeichen',
        });
      } else {
        this._toast.add({
          severity: 'contrast',
          summary: 'Bitte Passwort eingeben',
        });
      }
      return;
    }

    // Validate TOC
    if (!this.passwordForm.get('toc')?.value) {
      this._toast.add({
        severity: 'contrast',
        summary: 'Bitte AGB akzeptieren',
        detail: 'Du musst die AGB und Datenschutzerklärung akzeptieren',
      });
      return;
    }

    this.isAuthInitiated = true;
    this.passwordForm.disable();

    try {
      await firstValueFrom(
        this._auth.register(
          this.enteredEmail,
          this.passwordForm.value.password!,
        ),
      );
      this._toast.add({
        severity: 'success',
        summary: 'Registrierung erfolgreich',
        detail: 'Du hast dich erfolgreich registriert. Bitte bestätige deine E-Mail.',
      });

      setTimeout(() => {
        this._nav.navigateForward(['/auth/onboarding']);
      }, 2000);
    } catch (error: any) {
      // Email ist bereits registriert - zum Login wechseln
      if (error.code === 'auth/email-already-in-use') {
        this.isNewUser = false;
        this.setupPasswordFormForLogin();
        this.passwordForm.enable();
        this.isAuthInitiated = false;
        this._toast.add({
          severity: 'info',
          summary: 'Account existiert bereits',
          detail: 'Diese E-Mail ist bereits registriert. Bitte logge dich ein.',
        });
        return;
      }

      this.isAuthInitiated = false;
      this.passwordForm.enable();

      console.error('Error during registration:', error);

      const errorTranslation = firebaseAuthTranslation[error.code];

      if (errorTranslation?.toDev) console.error(errorTranslation.toDev);
      this._toast.add({
        severity: 'contrast',
        summary: 'Registrierung fehlgeschlagen',
        detail: errorTranslation?.toClient ?? 'Interner Serverfehler',
      });
    }
  }

  openPasswordResetSheet() {
    this.isPasswordResetRequestVisible = true;
  }

  async performPasswordResetRequest() {
    if (this.passwordResetRequestForm.invalid) {
      return;
    }
    this.isPasswordResetRequestInitiated = true;
    this.passwordResetRequestForm.disable();

    try {
      await firstValueFrom(
        this._auth.performPasswordReset(
          this.passwordResetRequestForm.value.email!,
        ),
      );
      this.isPasswordResetRequestVisible = false;
      this.isPasswordResetRequestInitiated = false;
      this.passwordResetRequestForm.enable();
      this._toast.add({
        severity: 'success',
        summary: 'Passwort zurücksetzen',
        detail:
          'Dir wurde eine E-Mail zum Zurücksetzen deines Passworts gesendet.',
      });
    } catch (error: any) {
      this.isPasswordResetRequestInitiated = false;
      this.passwordResetRequestForm.enable();

      const errorTranslation = firebaseAuthTranslation[error.code];

      if (errorTranslation?.toDev) console.error(errorTranslation.toDev);
      this._toast.add({
        severity: 'contrast',
        summary: 'Fehler beim Zurücksetzen des Passworts',
        detail: errorTranslation?.toClient ?? 'Interner Serverfehler',
      });
    }
  }

  async performPasswordReset() {
    if (this.passwordResetForm.invalid) {
      return;
    }

    this.isPasswordResetInitiated = true;
    this.passwordResetForm.disable();
    const oobCode = this._route.snapshot.queryParams['oobCode'];

    try {
      await firstValueFrom(
        this._auth.performPasswordResetWithCode(
          oobCode,
          this.passwordResetForm.value.password!,
        ),
      );
      this.isPasswordResetVisible = false;
      this._toast.add({
        severity: 'success',
        summary: 'Passwort zurücksetzen',
        detail:
          'Dein Passwort wurde erfolgreich zurückgesetzt, logge dich mit deinem neuen Passwort ein.',
      });
    } catch (error: any) {
      const errorTranslation = firebaseAuthTranslation[error.code];
      if (errorTranslation?.toDev) console.error(errorTranslation.toDev);
      this._toast.add({
        severity: 'contrast',
        summary: 'Fehler beim Zurücksetzen des Passworts',
        detail: errorTranslation?.toClient ?? 'Interner Serverfehler',
      });
    } finally {
      this.isPasswordResetVisible = false;
      this.isPasswordResetInitiated = false;
      this.passwordResetForm.enable();
      this._nav.navigateRoot([], {
        queryParams: {
          mode: null,
          oobCode: null,
          lang: null,
          apiKey: null,
        },
        queryParamsHandling: 'merge',
      });
    }
  }

  async verifyEmail() {
    const oobCode = this._route.snapshot.queryParams['oobCode'];
    this.isAuthInitiated = true;

    try {
      await firstValueFrom(this._auth.performEmailVerification(oobCode));
      this._toast.add({
        severity: 'success',
        summary: 'E-Mail verifiziert',
        detail: 'Deine E-Mail wurde erfolgreich verifiziert.',
      });

      setTimeout(() => {
        this._nav.navigateForward(['/onboarding']);
      }, 2000);
    } catch (error: any) {
      const errorTranslation = firebaseAuthTranslation[error.code];
      if (errorTranslation?.toDev) console.error(errorTranslation.toDev);
      this._toast.add({
        severity: 'contrast',
        summary: 'Fehler bei der E-Mail-Verifizierung',
        detail: errorTranslation?.toClient ?? 'Interner Serverfehler',
      });
      this._nav.navigateRoot([], {
        queryParams: {
          mode: null,
          oobCode: null,
          lang: null,
          apiKey: null,
        },
        queryParamsHandling: 'merge',
      });
      this.isAuthInitiated = false;
    }
  }

  async performGoogleLogin() {
    this.isGoogleLoginInitiated = true;
    try {
      await firstValueFrom(this._auth.performGoogleLogin());
      await this._profile.loadUserDetails();
      this._nav.navigateRoot(['/dashboard']);
    } catch (error: any) {
      this.isGoogleLoginInitiated = false;
      console.error('Google Login Error:', error);
      const errorTranslation = firebaseAuthTranslation[error.code];
      if (errorTranslation?.toDev) console.error(errorTranslation.toDev);
      this._toast.add({
        severity: 'contrast',
        summary: 'Fehler beim Google Login',
        detail: errorTranslation?.toClient ?? 'Interner Serverfehler',
      });
    }
  }

  async performAppleLogin() {
    this.isAppleLoginInitiated = true;
    try {
      await firstValueFrom(this._auth.performAppleLogin());
      await this._profile.loadUserDetails();
      this._nav.navigateRoot(['/dashboard']);
    } catch (error: any) {
      this.isAppleLoginInitiated = false;
      console.error('Apple Login Error:', error);
      const errorTranslation = firebaseAuthTranslation[error.code];
      if (errorTranslation?.toDev) console.error(errorTranslation.toDev);
      this._toast.add({
        severity: 'contrast',
        summary: 'Fehler beim Apple Login',
        detail: errorTranslation?.toClient ?? 'Interner Serverfehler',
      });
    }
  }

  get isAndroid(): boolean {
    return this.platform.is('android');
  }
}